import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { trackWaClick } from "@/lib/api";
import { resolveMedia } from "@/lib/media";

export default function Products() {
  const { content, products } = useSite();
  const ps = content?.products_section || {};
  const waNumber = (content?.contact?.whatsapp || "3624723836").replace(/\D/g, "");

  const waFor = (name) =>
    `https://wa.me/54${waNumber}?text=${encodeURIComponent(`Hola AUREX, quiero información sobre la ${name}.`)}`;

  return (
    <section id="productos" className="relative mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
      <Reveal className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <span className="font-mono-jb text-xs uppercase tracking-[0.35em] text-[color:var(--aurex-accent-soft)]">
            {ps.overline || "Equipos"}
          </span>
          <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-[color:var(--aurex-text)] sm:text-5xl">
            {ps.title || "Elegí tu próxima herramienta."}
          </h2>
        </div>
        <p className="max-w-sm text-[color:var(--aurex-muted)]">
          {ps.subtitle || "Cada AUREX está pensada para rendir sin límites. Consultá disponibilidad y precios."}
        </p>
      </Reveal>

      <Stagger className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <StaggerItem key={p.id}>
            <motion.div
              whileHover={{ y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-white/10 bg-[color:var(--aurex-surface)]"
              data-testid={`product-card-${p.id}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={resolveMedia(p.image)}
                  alt={p.name}
                  className="h-56 w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 overlay-surface-top" />
                {p.badge && (
                  <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono-jb text-[10px] uppercase tracking-[0.18em] text-[color:var(--aurex-text)] backdrop-blur">
                    {p.badge}
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-display text-2xl font-bold text-[color:var(--aurex-text)]">{p.name}</h3>
                <p className="mt-1 text-sm text-[color:var(--aurex-accent-soft)]">{p.tagline}</p>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--aurex-muted)]">{p.description}</p>

                <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/10 pt-5">
                  {(p.specs || []).map((s, i) => (
                    <div key={i}>
                      <div className="font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-600">
                        {s.label}
                      </div>
                      <div className="text-sm text-[color:var(--aurex-text)]">{s.value}</div>
                    </div>
                  ))}
                </div>

                <a
                  data-testid={`product-wa-${p.id}`}
                  href={waFor(p.name)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackWaClick("product", p.name)}
                  className="group/btn mt-6 flex items-center justify-between rounded-full border border-white/15 px-5 py-3 text-sm font-medium text-[color:var(--aurex-text)] transition-colors duration-300 hover:border-[color:var(--aurex-accent)] hover:text-[color:var(--aurex-accent-soft)]"
                >
                  Consultar · {p.price}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </div>
            </motion.div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
